import { useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { isRefreshPollingSlow, refreshPollInterval } from '../feeding/liveRefreshPolling';

import { getFeedingDashboardOverview } from './api';
import { mapFeedingDashboardOverviewToDomain } from './mappers';
import type { FeedingDashboardOverview, FeedingDashboardQuery } from './types';

export const FEEDING_DASHBOARD_KEY = 'feeding-dashboard';

export interface UseFeedingDashboardResult {
    data?: FeedingDashboardOverview;
    isLoading: boolean;
    /** True while the prior query's data stands in until this query's first response. */
    isPreviousData: boolean;
    error: unknown;
    isFetching: boolean;
    refetch: () => void;
    /**
     * True once the live-refresh poll budget's fast and slow tiers
     * (`liveRefreshPolling.ts`) are spent while the last response still reports
     * `refreshing`. Polling itself does not stop here: it drops to the slow trailing
     * interval and keeps going until `refreshing` turns false. Resets when the query
     * identity (location, date range, pen filter or pellet flag) changes.
     */
    pollingSlow: boolean;
}

export interface UseFeedingDashboardOptions {
    /**
     * Set false to skip the request entirely. Used when the pen filter is empty: the
     * answer for "no pens" is knowably nothing, so asking is wasted work, and an
     * omitted `cageIds` means "every pen" to the API rather than "none".
     */
    enabled?: boolean;
}

export function useFeedingDashboard(
    locationId: number,
    query?: FeedingDashboardQuery,
    options?: UseFeedingDashboardOptions
): UseFeedingDashboardResult {
    const [pollingSlow, setPollingSlow] = useState(false);

    // includeFallthrough is part of the identity: with and without it the API returns
    // different pellet fields, and the flag can resolve after the first request.
    const queryKey = [
        FEEDING_DASHBOARD_KEY,
        locationId,
        query?.from,
        query?.to,
        query?.cageIds,
        query?.includeFallthrough === true,
    ];
    // A different query identity (location, date range, pen filter or pellet flag)
    // gets its own fresh `Query` instance and its own poll budget, so a flag raised
    // for the previous identity must not carry over. Comparing a signature of the key, rather
    // than an effect on the individual parts, means an array like `cageIds` that is
    // recreated with the same values on every render does not force a reset, and the
    // reset is visible in the same render that changed identity rather than one tick
    // later. This is React's documented pattern for adjusting state in response to a
    // derived value changing, applied during render rather than in an effect.
    const keySignature = JSON.stringify(queryKey);
    const prevKeySignatureRef = useRef(keySignature);
    if (prevKeySignatureRef.current !== keySignature) {
        prevKeySignatureRef.current = keySignature;
        setPollingSlow(false);
    }

    const result = useQuery({
        queryKey,
        queryFn: () => getFeedingDashboardOverview(locationId, query),
        enabled: Number.isFinite(locationId) && locationId > 0 && (options?.enabled ?? true),
        staleTime: 60_000,
        placeholderData: keepPreviousData,
        // `dataUpdateCount` and `errorUpdateCount` are react-query's own counts of
        // settled fetches (successful and failed respectively) for this query identity;
        // `refreshPollInterval` and `isRefreshPollingSlow` treat their sum the same way
        // the original single-cap version did (see their own doc comments for why
        // failures count). This callback runs outside React's render phase (query
        // timer/update callbacks), so setting `pollingSlow` here does not conflict with
        // rendering.
        refetchInterval: (activeQuery) => {
            const refreshing = activeQuery.state.data?.data.feeding?.refreshing ?? false;
            const settledFetches =
                activeQuery.state.dataUpdateCount + activeQuery.state.errorUpdateCount;
            if (refreshing && isRefreshPollingSlow(settledFetches)) {
                setPollingSlow(true);
            }
            return refreshPollInterval(refreshing, settledFetches);
        },
        refetchIntervalInBackground: false,
    });

    const data = useMemo(
        () =>
            result.data?.data ? mapFeedingDashboardOverviewToDomain(result.data.data) : undefined,
        [result.data?.data]
    );

    return {
        data,
        isLoading: result.isPending,
        isPreviousData: result.isPlaceholderData,
        error: result.error,
        isFetching: result.isFetching,
        refetch: result.refetch,
        pollingSlow,
    };
}
